import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AppShell from '../components/AppShell';
import RequestShowingModal from '../components/RequestShowingModal';
import PaymentModal from '../components/PaymentModal';
import { useAuth } from '../context/AuthContext';
import { mapL2ToCardShape } from '../utils/listingShape';
import HomesLockedPage, { HOMES_LOCKED_STATUSES } from '../components/HomesLockedPage';
import { SHOWING_OFFER_BLOCKED_STAGES } from '../constants/statusColors';
import { OfferRequestModule } from './Homes';
import { formatRate } from '../constants/rateFormat';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const RECENTLY_VIEWED_KEY = 'doorly_recently_viewed';

// ── Formatters ────────────────────────────────────────────────────────────────
function fmt$(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}

// Active uses the Doorly brand-green pill (#acf44e bg + #111827 text)
// — the same combo the Homes.js card pills + ReviewModule Accept
// buttons use. The `style` field overrides bg/text for the photo
// pill render path; bg/text shorthand remains for surfaces that
// only need a dot/swatch.
const STATUS_CONFIG = {
  Active:       { bg: 'bg-green-500',  text: 'text-green-700',  bgLight: 'bg-green-100',
                  pillStyle: { background: '#acf44e', color: '#111827' } },
  Pending:      { bg: 'bg-yellow-500', text: 'text-yellow-700', bgLight: 'bg-yellow-100' },
  'Coming Soon':{ bg: 'bg-blue-500',   text: 'text-blue-700',   bgLight: 'bg-blue-100'   },
};

// getDownPaymentTier removed — MortgageEstimate now reads downPct
// from the Layer 2 smartEstimate overlay (server-sourced from
// users.accepted_down_pct → down_payment_pct). No client-side tier
// math needed.


// MortgageEstimate (3b commit 3) — consumes the Layer 2 smartEstimate
// overlay attached by getListingForBorrower. Single source of truth:
// the SAME monthlyPayment + breakdown the borrower saw on the card
// grid renders here on the detail page. No client-side recompute, no
// hardcoded rate fallback, no PMI. Rate comes from
// users.atlas_final_rate via the server-side overlay.
//
// THREE STATES
//   smartEstimate present                 → full PITI block + breakdown
//   listing exists but smartEstimate null → "Complete pricing" prompt
//     (atlas_final_rate or down_payment_pct missing — borrower is
//      pre-pricing-acceptance; the Dashboard hero has the CTA).
//   no listing                            → null render
function MortgageEstimate({ listing }) {
  if (!listing || !listing.price) return null;
  const se = listing.smartEstimate;

  // Pre-pricing state — no Atlas-priced rate yet, no PMI placeholder,
  // no guessed 7% fallback. Surface a calm prompt that the rate isn't
  // available rather than a misleading number.
  if (!se || !se.monthlyPayment) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <h3 className="text-base font-bold text-black mb-2">Mortgage Estimate</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Your personalized estimate will appear here once you accept your SmartEstimate on the Dashboard.
        </p>
      </div>
    );
  }

  const downAmt   = Math.round(Number(listing.price) * (Number(se.downPct) / 100));
  const loanAmt   = Math.max(0, Number(listing.price) - downAmt);
  const bd        = se.breakdown || {};

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#eff3fe' }}>
          <svg className="w-5 h-5" style={{ color: '#3a6ad9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-bold text-black">Mortgage Estimate</h3>
          <p className="text-xs text-[#3a6ad9] font-medium">Based on your SmartEstimate</p>
        </div>
      </div>
      <div className="bg-[#eff3fe] rounded-xl px-5 py-4 mb-4 text-center">
        <p className="text-xs text-gray-500 mb-0.5">Estimated Monthly Payment (PITI{bd.hoa > 0 ? ' + HOA' : ''})</p>
        <p className="text-3xl font-bold" style={{ color: '#3a6ad9' }}>{fmt$(se.monthlyPayment)}</p>
      </div>
      <div className="divide-y divide-gray-100 text-sm">
        {[
          { label: 'Listing Price',                              value: fmt$(listing.price) },
          { label: `Down Payment (${se.downPct}%)`,              value: fmt$(downAmt) },
          { label: 'Loan Amount',                                value: fmt$(loanAmt) },
          { label: 'Your SmartEstimate™ Rate',                   value: formatRate(se.rate) },
          { label: 'Loan Term',                                  value: '30 years' },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between py-2.5">
            <span className="text-gray-500">{label}</span>
            <span className="font-semibold text-black">{value}</span>
          </div>
        ))}
      </div>
      {/* Breakdown — PITI components (NO PMI; AFM is non-QM, no MI
          binds). Shown as a second tier under the main PITI line
          rather than mixed in to keep the headline figure prominent. */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2">Monthly breakdown</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-gray-400">P&amp;I</p>
            <p className="font-semibold text-black">{fmt$(bd.pi || 0)}</p>
          </div>
          <div>
            <p className="text-gray-400">Property tax</p>
            <p className="font-semibold text-black">{fmt$(bd.tax || 0)}</p>
          </div>
          <div>
            <p className="text-gray-400">Insurance</p>
            <p className="font-semibold text-black">{fmt$(bd.ins || 0)}</p>
          </div>
          {bd.hoa > 0 && (
            <div>
              <p className="text-gray-400">HOA</p>
              <p className="font-semibold text-black">{fmt$(bd.hoa)}</p>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-4 leading-relaxed">
        Estimate based on the rate Doorly priced for your file. Property tax (1.8%/yr) and homeowners insurance (0.5%/yr) are program assumptions; actual escrow figures vary by county and carrier. Contact the Doorly team for a precise quote.
      </p>
    </div>
  );
}

// ── Main HomeDetail ───────────────────────────────────────────────────────────
export default function HomeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [listing, setListing]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [photoIdx, setPhotoIdx]       = useState(0);
  const [saved, setSaved]             = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [showingModal, setShowingModal] = useState(false);
  // MLS attribution profiles — mirror of the Homes.js footer pattern.
  // Loaded once per detail-page view via GET /api/homes/mls-attribution
  // (backend caches 24h). Compliance: any page rendering MLS data must
  // display the originating MLS's logo + disclaimer.
  const [mlsProfiles, setMlsProfiles] = useState([]);
  const [offerModal, setOfferModal]   = useState(false);
  // Full loan-details payload — required by OfferRequestModule for the
  // Step 2 loan-comparison columns + Step 3 down-payment ack. The fetch
  // below already runs for stage-gating; we keep the whole response so
  // the wizard sees max_approval_amount, atlas_*, smartestimate_option,
  // resale_adjustment, etc.
  const [loanDetails, setLoanDetails] = useState(null);
  // PaymentModal plumbing — same handoff pattern as Homes.js:
  // OfferRequestModule emits the normalized offer payload, we either
  // POST it directly (fee waived) or stash it + open the Stripe
  // PaymentModal. On success the modal calls back with the
  // payment_intent_id and we POST the offer.
  const [showOfferPayment, setShowOfferPayment] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null);
  const [offerSuccess, setOfferSuccess] = useState(false);
  // approvedRate state removed — MortgageEstimate now reads
  // listing.smartEstimate.rate from the Layer 2 overlay (server-side
  // sourced from users.atlas_final_rate). No client-side rate state
  // needed on this page.
  // Active-offer guard — 3c commit 2 — now sourced from the shared
  // SHOWING_OFFER_BLOCKED_STAGES constant in constants/statusColors.js.
  // The previous local list here was MISSING 'offer_requested', which
  // meant a borrower in offer_requested could double-book from this
  // detail page even though Homes.js (which had the correct list)
  // hid the button. The shared constant fixes that.
  const [applicationStatus, setApplicationStatus] = useState(null);
  // smartestimate_accepted gates the page alongside HOMES_LOCKED_STATUSES.
  // Mirrors Homes.js: borrower is unlocked when EITHER smartestimate_-
  // accepted OR application_status is past the pre-pricing tier.
  // Loading flag starts true so the page doesn't flash-unlocked
  // during the loan-details fetch.
  const [smartestimateAccepted, setSmartestimateAccepted] = useState(false);
  const [stageLoading, setStageLoading] = useState(true);
  const hasActiveOffer = SHOWING_OFFER_BLOCKED_STAGES.includes(applicationStatus);

  // ── Fetch listing ───────────────────────────────────────────────────────────
  // 3b: backend GET /api/homes/:id now returns the Layer 2 normalized
  // listing shape (Doorly camelCase + overlay). Pass through the
  // shared mapL2ToCardShape adapter so the JSX below reads identical
  // field names to the card grid (sqft, year_built, photos[i]=URL
  // string, etc.) without per-page mapping.
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/homes/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setListing(mapL2ToCardShape(r.data.listing));
        // Track recently viewed
        try {
          const existing = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
          const filtered = existing.filter((vid) => vid !== id);
          localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify([id, ...filtered].slice(0, 10)));
          setRecentlyViewed([id, ...filtered].slice(0, 4));
        } catch (_) {}
      })
      .catch(() => navigate('/homes'))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  // ── Fetch all listings for recently-viewed section ──────────────────────────
  // Repointed to /api/homes/listings (Layer 2) — legacy /api/homes
  // deleted in 3b commit 062679e. Layer 2 returns shaped objects
  // (listingId, address.full, etc.); mapL2ToCardShape brings them
  // into the same snake_case-ish shape this page's existing
  // recently-viewed JSX expects (listing.id, listing.address.street).
  useEffect(() => {
    axios.get(`${API_URL}/api/homes/listings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAllListings((r.data.listings || []).map(mapL2ToCardShape).filter(Boolean)))
      .catch(() => {});
  }, [token]);

  // ── Fetch MLS attribution (cached 24h server-side) ────────────────
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/homes/mls-attribution`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setMlsProfiles(Array.isArray(r.data?.profiles) ? r.data.profiles : []))
      .catch(() => setMlsProfiles([]));
  }, [token]);

  // ── Fetch loan details for active-offer guard + stage-gate ──
  // Rate fetch removed — MortgageEstimate reads it from the Layer 2
  // overlay (listing.smartEstimate.rate) now. application_status +
  // smartestimate_accepted feed (a) the active-offer guard on the
  // action buttons and (b) the stage-gate that renders
  // HomesLockedPage on locked stages (mirror of Homes.js gate so
  // deep-linking a /homes/:id URL while locked surfaces the same
  // page the list view would).
  useEffect(() => {
    axios.get(`${API_URL}/api/users/me/loan-details`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data?.application_status) setApplicationStatus(r.data.application_status);
        if (r.data?.smartestimate_accepted) setSmartestimateAccepted(true);
        setLoanDetails(r.data || null);
      })
      .catch(() => {})
      .finally(() => setStageLoading(false));
  }, [token]);

  // ── Check if saved ──────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_URL}/api/homes/saved`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const ids = new Set((r.data.saved || []).map((s) => String(s.listing_id)));
        setSaved(ids.has(String(id)));
      })
      .catch(() => {});
  }, [id, token]);

  // ── Toggle save ─────────────────────────────────────────────────────────────
  const toggleSave = async () => {
    if (saved) {
      setSaved(false);
      try { await axios.delete(`${API_URL}/api/homes/save/${id}`, { headers: { Authorization: `Bearer ${token}` } }); } catch (_) {}
    } else {
      setSaved(true);
      try { await axios.post(`${API_URL}/api/homes/save`, { listing_id: id }, { headers: { Authorization: `Bearer ${token}` } }); } catch (_) {}
    }
  };

  // ── Stage gate (3b commit 4) ──────────────────────────────
  // Mirrors Homes.js logic: unlocked when EITHER smartestimate_-
  // accepted is true OR the stage is past the pre-pricing tier.
  // Defer the decision while loanDetails is still loading so we
  // never flash-render the page to a locked borrower.
  if (stageLoading) {
    return (
      <AppShell>
        <div className="animate-pulse space-y-4">
          <div className="bg-gray-200 rounded-2xl h-72 w-full" />
        </div>
      </AppShell>
    );
  }
  const isHomesUnlocked = smartestimateAccepted
    || (applicationStatus && !HOMES_LOCKED_STATUSES.includes(applicationStatus));
  if (!isHomesUnlocked) {
    return <HomesLockedPage />;
  }

  if (loading) {
    return (
      <AppShell>
        <div className="animate-pulse space-y-4">
          <div className="bg-gray-200 rounded-2xl h-72 w-full" />
          <div className="h-8 bg-gray-200 rounded w-2/3" />
          <div className="h-4 bg-gray-100 rounded w-1/2" />
        </div>
      </AppShell>
    );
  }

  if (!listing) return null;

  const fullAddress = `${listing.address.street}, ${listing.address.city}, ${listing.address.state} ${listing.address.zip}`;
  const statusCfg = STATUS_CONFIG[listing.status] || STATUS_CONFIG['Active'];

  // Recently viewed excluding current
  const recentHomes = recentlyViewed
    .filter((vid) => vid !== id)
    .map((vid) => allListings.find((l) => l.id === vid))
    .filter(Boolean)
    .slice(0, 4);

  return (
    <AppShell>
      {/* Back button */}
      <button
        onClick={() => navigate('/homes')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Search
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: photos + details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Photo gallery */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Main photo */}
            <div className="relative bg-gray-100" style={{ paddingBottom: '56.25%' }}>
              <img
                src={listing.photos[photoIdx]}
                alt={listing.address.street}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Status pill — Active renders with the Doorly brand-green
                  inline style (#acf44e bg + #111827 text); other statuses
                  fall back to the Tailwind bg shorthand. */}
              {statusCfg.pillStyle ? (
                <span
                  className="absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full"
                  style={statusCfg.pillStyle}
                >
                  {listing.status}
                </span>
              ) : (
                <span className={`absolute top-4 left-4 ${statusCfg.bg} text-white text-xs font-bold px-3 py-1.5 rounded-full`}>
                  {listing.status}
                </span>
              )}
              {/* Featured pill — when present, shifts left of the heart
                  so both can sit in the top-right corner without overlap. */}
              {listing.is_promoted && (
                <span className="absolute top-4 right-16 bg-[#fffd5b] text-black text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Featured
                </span>
              )}
              {/* Save-to-favorites heart — mirrors the Homes.js ListingCard
                  pattern (white circular button, filled red when saved,
                  outline-gray otherwise). Shares toggleSave with the
                  sidebar "Save to Favorites" button so both surfaces stay
                  in sync via the `saved` state. */}
              <button
                onClick={toggleSave}
                aria-label={saved ? 'Remove from favorites' : 'Save to favorites'}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
              >
                <svg className="w-5 h-5" fill={saved ? '#ff082f' : 'none'} stroke={saved ? '#ff082f' : '#9ca3af'} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              {/* Nav arrows */}
              {listing.photos.length > 1 && (
                <>
                  <button
                    onClick={() => setPhotoIdx((i) => (i - 1 + listing.photos.length) % listing.photos.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setPhotoIdx((i) => (i + 1) % listing.photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow-md hover:bg-white transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
              {/* Photo count */}
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">
                {photoIdx + 1} / {listing.photos.length}
              </span>
            </div>
            {/* Thumbnail strip */}
            {listing.photos.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {listing.photos.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setPhotoIdx(i)}
                    className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === photoIdx ? 'border-[#3a6ad9]' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Price + address */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-3xl font-bold text-black">{fmt$(listing.price)}</p>
                <p className="text-gray-700 font-medium mt-0.5">{listing.address.street}</p>
                <p className="text-gray-500 text-sm">{listing.address.city}, {listing.address.state} {listing.address.zip}</p>
              </div>
              {listing.hoa_fee && (
                <div className="text-right">
                  <p className="text-xs text-gray-400">HOA</p>
                  <p className="text-sm font-semibold text-gray-700">{fmt$(listing.hoa_fee)}/mo</p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-5 pt-5 border-t border-gray-100">
              {[
                { label: 'Beds',       value: listing.beds,              icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                { label: 'Baths',      value: listing.baths,             icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z' },
                { label: 'Sq Ft',      value: listing.sqft.toLocaleString(), icon: 'M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4' },
                { label: 'Year Built', value: listing.year_built,         icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5' },
                { label: 'Garage',     value: listing.garage ? `${listing.garage} car` : 'No', icon: 'M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0' },
              ].map(({ label, value, icon }) => (
                <div key={label} className="text-center bg-gray-50 rounded-xl p-3">
                  <svg className="w-5 h-5 mx-auto mb-1 text-[#3a6ad9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
                  </svg>
                  <p className="text-base font-bold text-black">{value}</p>
                  <p className="text-xs text-gray-400">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <h3 className="text-base font-bold text-black mb-3">About This Home</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{listing.description}</p>
          </div>

          {/* Property Details */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
            <h3 className="text-base font-bold text-black mb-4">Property Details</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ['Property Type',  listing.property_type],
                ['Year Built',     listing.year_built],
                ['Square Footage', `${listing.sqft.toLocaleString()} sq ft`],
                ['Bedrooms',       listing.beds],
                ['Bathrooms',      listing.baths],
                ['Garage',         listing.garage ? `${listing.garage} car` : 'No garage'],
                listing.lot_size && ['Lot Size', `${listing.lot_size.toLocaleString()} sq ft`],
                listing.hoa_fee && ['HOA Fee', `${fmt$(listing.hoa_fee)}/month`],
                ['Days on Market', listing.days_on_market || 'New'],
                ['Listing Status', listing.status],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className="flex flex-col">
                  <span className="text-gray-400 text-xs">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Old Location placeholder removed (duplicate of the real
              map + Get Directions card rendered in the right panel
              below from 3c commit 4). The TODO 'Google Maps embed'
              copy was misleading after the real embed shipped. */}
        </div>

        {/* Right: actions + calculator */}
        <div className="space-y-5">
          {/* Action buttons */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-2xl font-bold text-black mb-1">{fmt$(listing.price)}</p>
            <p className="text-sm text-gray-500 mb-5">{listing.address.city}, {listing.address.state}</p>

            <div className="space-y-2.5">
              {/* Stage-based button surface (3c follow-up).
                  · home_shopping  → 3 buttons: Schedule Showing,
                                    Start Purchase, Save to Favorites
                  · everything past home_shopping (hasActiveOffer per
                                    SHOWING_OFFER_BLOCKED_STAGES) →
                                    SINGLE 'Go to Dashboard' button.
                                    Replaces the clipping 'You have
                                    an active offer — View Dashboard'
                                    copy that wrapped awkwardly on
                                    mobile. Save Home + Back to Search
                                    intentionally hidden post-shopping
                                    too — the dashboard is the focal
                                    point once a deal is in motion. */}
              {hasActiveOffer ? (
                <a
                  href="/dashboard"
                  className="btn-primary btn-full text-white"
                  style={{
                    backgroundColor: '#3a6ad9',
                    textDecoration:  'none',
                    minHeight:       '44px',   // iOS HIG min tap target
                  }}
                >
                  <span>Go to Dashboard</span>
                  <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
                </a>
              ) : (
                <>
                  <button
                    onClick={() => setShowingModal(true)}
                    className="btn-primary btn-full text-white"
                    style={{ backgroundColor: '#3a6ad9', minHeight: '44px' }}
                  >
                    <span>Schedule Showing</span>
                    <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
                  </button>
                  <button
                    onClick={() => setOfferModal(true)}
                    className="btn-primary btn-full text-black"
                    style={{ backgroundColor: '#fffd5b', minHeight: '44px' }}
                  >
                    <span>Start Purchase</span>
                    <span className="btn-arrow" style={{ backgroundColor: '#000' }}><svg viewBox="0 0 24 24" fill="none" stroke="#fffd5b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg></span>
                  </button>
                  <button
                    onClick={toggleSave}
                    className={`btn-secondary btn-full border transition-colors ${
                      saved ? 'border-red-200 bg-red-50 text-red-600' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{
                      display:        'inline-flex',
                      alignItems:     'center',
                      justifyContent: 'center',
                      gap:            '8px',         // ← icon/text spacing fix
                      minHeight:      '44px',
                    }}
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{saved ? 'Saved to Favorites' : 'Save to Favorites'}</span>
                  </button>
                </>
              )}
              {!hasActiveOffer && (
                <button
                  onClick={() => navigate('/homes')}
                  className="btn-secondary btn-full border border-gray-200 text-gray-600 hover:bg-gray-50"
                  style={{ minHeight: '44px' }}
                >
                  Back to Search
                </button>
              )}
            </div>
          </div>

          {/* Mortgage estimate */}
          {/* 3b: MortgageEstimate now reads listing.smartEstimate
              (Layer 2 overlay attached by getListingForBorrower).
              approvedRate prop dropped — rate sources from the
              overlay only, server-side, no client recompute. */}
          <MortgageEstimate listing={listing} />

          {/* Location map + driving directions — 3c commit 4.
              Embedded map pinned to the listing's lat/lng via the
              standard Google Maps embed (no API key needed for the
              embed URL — only the JS API requires one). Get Directions
              deep-links to https://www.google.com/maps/dir/?destination=
              LAT,LNG which on mobile opens the native Maps app and on
              desktop opens google.com/maps with the route. No origin —
              the user's own location fills it in. */}
          {listing.lat != null && listing.lng != null && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-5 sm:p-6 border-b border-gray-100">
                <h3 className="text-base font-bold text-black mb-1">Location</h3>
                <p className="text-xs text-gray-500">{fullAddress}</p>
              </div>
              <div style={{ position: 'relative', paddingBottom: '50%', background: '#f9fafb' }}>
                <iframe
                  title="Listing Location"
                  src={`https://www.google.com/maps?q=${listing.lat},${listing.lng}&z=15&output=embed`}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%', border: 0,
                  }}
                  loading="lazy"
                />
              </div>
              <div className="p-5 sm:p-6">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${listing.lat},${listing.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                  style={{ background: '#3a6ad9', color: '#fff' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Get Directions →
                </a>
                <p className="text-xs text-gray-400 mt-3">
                  Opens Google Maps from your current location to this address.
                </p>
              </div>
            </div>
          )}

          {/* "Days on market + status" card removed — the listing_date
              field is not consistently populated by the AnyProp HAR feed
              (frequently null), which rendered "Listed Invalid Date"
              when new Date(undefined).toLocaleDateString() ran. The
              status pill on the photo (top-left) already conveys
              "Active"; days-on-market when populated lives in the
              Listing Data section above. Restore this card only when
              listing.listing_date can be guaranteed non-null. */}

          {/* MLS attribution — compliance requirement: any page
              rendering MLS data must display the originating MLS's
              logo + disclaimer. Mirrors the Homes.js footer block
              (lines ~2334-2354) — one card per originating-system
              profile. Graceful no-render when the upstream
              attribution endpoint is down (mlsProfiles stays []). */}
          {mlsProfiles.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Listing Data Provided By
              </h3>
              {mlsProfiles.map((p, i) => (
                <div key={p.originatingSystemName || i} className={i > 0 ? 'mt-4 pt-4 border-t border-gray-100' : ''}>
                  <div className="flex items-start gap-3 mb-2">
                    {p.logoUrl ? (
                      <img
                        src={p.logoUrl}
                        alt={p.displayName || p.originatingSystemName || 'MLS'}
                        style={{ height: 28, width: 'auto' }}
                      />
                    ) : null}
                    {p.displayName ? (
                      <div className="text-sm font-semibold text-gray-700" style={{ paddingTop: p.logoUrl ? 4 : 0 }}>
                        {p.displayName}
                      </div>
                    ) : null}
                  </div>
                  {p.disclaimer ? (
                    <p className="text-xs text-gray-500 leading-relaxed">{p.disclaimer}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recently Viewed */}
      {recentHomes.length > 0 && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Recently Viewed</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {recentHomes.map((home) => (
              <Link
                key={home.id}
                to={`/homes/${home.id}`}
                className="flex-shrink-0 w-48 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <img src={home.photos[0]} alt={home.address.street} className="w-full h-28 object-cover" />
                <div className="p-3">
                  <p className="text-sm font-bold text-black">{fmt$(home.price)}</p>
                  <p className="text-xs text-gray-500 truncate">{home.address.street}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {showingModal && <RequestShowingModal listing={listing} onClose={() => setShowingModal(false)} token={token} />}
      {/* Offer flow — same 3-step wizard the card-grid uses
          (OfferRequestModule imported from Homes.js). The expanded
          listing view previously had its own single-screen OfferModal
          which diverged from the card flow on copy, loan-comparison
          columns, and acknowledgments. Reused now so the borrower
          sees the identical flow regardless of entry point. */}
      {offerModal && (
        <OfferRequestModule
          listing={listing}
          loanDetails={loanDetails}
          feeWaived={!!(loanDetails?.offer_fee_waived || loanDetails?.offer_initiation_fee_paid || loanDetails?.is_demo)}
          onClose={() => setOfferModal(false)}
          onPaymentRequest={(payload) => {
            setOfferModal(false);
            setPendingOffer(payload);
            setShowOfferPayment(true);
          }}
          onWaivedSubmit={async (payload) => {
            try {
              await axios.post(`${API_URL}/api/homes/offer`, {
                ...payload,
                offer_initiation_payment_method: 'waived',
              }, { headers: { Authorization: `Bearer ${token}` } });
              setOfferModal(false);
              setOfferSuccess(true);
              setTimeout(() => navigate('/dashboard'), 1500);
            } catch (err) {
              console.error('[Offer] Waived submit error:', err.response?.data || err.message);
              alert(err.response?.data?.error || 'Could not submit your offer. Please try again or contact support.');
            }
          }}
        />
      )}
      <PaymentModal
        isOpen={showOfferPayment}
        onClose={() => { setShowOfferPayment(false); setPendingOffer(null); }}
        type="offer_fee"
        extraBody={pendingOffer
          ? {
              listing_id: pendingOffer.listing_id,
              address:    pendingOffer.address,
              list_price: pendingOffer.list_price,
              notes:      pendingOffer.notes,
              listing_url: pendingOffer.listing_url,
            }
          : {}}
        onSuccess={async (paymentResult) => {
          setShowOfferPayment(false);
          try {
            if (pendingOffer) {
              await axios.post(`${API_URL}/api/homes/offer`, {
                ...pendingOffer,
                offer_initiation_payment_method: 'stripe',
                payment_intent_id: paymentResult?.payment_intent_id,
              }, { headers: { Authorization: `Bearer ${token}` } });
            }
          } catch (err) {
            console.error('[Offer] Post-payment submit error:', err.message);
          }
          setPendingOffer(null);
          setOfferSuccess(true);
          setTimeout(() => navigate('/dashboard'), 1500);
        }}
      />
      {offerSuccess && (
        <div
          className="safe-modal-overlay"
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 16, padding: '32px 28px',
              maxWidth: 420, textAlign: 'center',
              boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
            }}
          >
            <div style={{ fontSize: 44, marginBottom: 8 }}>🎉</div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
              Offer Request Submitted!
            </h3>
            <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
              We&rsquo;ll follow up with you shortly. Redirecting you to your dashboard…
            </p>
          </div>
        </div>
      )}
    </AppShell>
  );
}
